/**
 * Configurações de um módulo (vendas ou compras).
 *
 * O QUE ESTA TELA RESPONDE
 *
 * "Por que não vejo compras?" tem duas respostas completamente diferentes, e
 * antes as duas chegavam ao suporte como "a plataforma está quebrada":
 *
 *   não contratado — é comercial. Ninguém na operação resolve.
 *   bloqueado      — é carga. Tem tabela e coluna com nome, e alguém resolve.
 *
 * Por isso os módulos não contratados aparecem aqui em vez de serem omitidos:
 * omitir devolve a pergunta sem resposta.
 *
 * O QUE ELA AINDA NÃO TEM
 *
 * Os ajustes do módulo (horizonte de compra, dias de cobertura). Existe um
 * catálogo de 54 parâmetros em `src/canonical/parametros.py`, mas nenhum é
 * lido pelo motor hoje — para a Climar, o plano de compra vem calculado do
 * ERP deles e é apenas ingerido. Colocar os campos aqui agora seria um painel
 * com botões desligados, então a tela diz isso em vez de fingir.
 */
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, Box, Chip, Paper, Skeleton, Stack, Typography,
} from "@mui/material";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import RemoveCircleOutlineRounded from "@mui/icons-material/RemoveCircleOutlineRounded";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";

type Motivo = { capacidade: string; tabela: string; detalhe: string };
type EstadoDoModulo = {
  chave: string;
  rotulo: string;
  contratado: boolean;
  estado: "ativo" | "bloqueado" | "nao_contratado";
  explicacao: string;
  motivos: Motivo[];
  tabelas: string[];
};

const VISUAL = {
  ativo: { cor: "success.main", Icon: CheckCircleRounded, rotulo: "Funcionando" },
  bloqueado: { cor: "warning.main", Icon: ErrorOutlineRounded, rotulo: "Falta dado" },
  nao_contratado: { cor: "text.disabled", Icon: RemoveCircleOutlineRounded, rotulo: "Não contratado" },
} as const;

export function Modulo() {
  const { modulo = "" } = useParams();
  const api = useApi();
  const { tenant } = useTenant();

  const q = useQuery({
    queryKey: ["modulos", tenant],
    queryFn: () => api.call<{ modulos: EstadoDoModulo[] }>("/api/modulos"),
    enabled: !!tenant,
  });

  const atual = q.data?.modulos.find((m) => m.chave === modulo);
  const outros = (q.data?.modulos ?? []).filter((m) => m.chave !== modulo);
  const visual = VISUAL[atual?.estado ?? "nao_contratado"];
  const Icon = visual.Icon;

  return (
    <>
      {q.error && <AvisoDeLeitura oQue="a situação dos módulos" />}
      <PageHeader
        eyebrow="Módulo"
        title={atual?.rotulo || (modulo === "vendas" ? "Vendas" : "Compras")}
        subtitle="Situação deste módulo na sua empresa e o que ele usa de dado."
      />

      {q.isLoading && <Skeleton variant="rounded" height={160} sx={{ mb: 2 }} />}

      {atual && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
            <Icon sx={{ color: visual.cor }} />
            <Typography sx={{ fontWeight: 700 }}>{visual.rotulo}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 820, lineHeight: 1.6 }}>
            {atual.explicacao}
          </Typography>

          {atual.estado === "bloqueado" && atual.motivos.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                O que falta na carga
              </Typography>
              {atual.motivos.map((m, i) => (
                <Box key={i} sx={{ fontSize: 13.5, mb: 0.25 }}>
                  {m.detalhe}
                  {m.tabela && (
                    <Chip
                      size="small" variant="outlined" label={m.tabela}
                      sx={{ ml: 1, height: 20, fontSize: 11, fontFamily: "ui-monospace, monospace" }}
                    />
                  )}
                </Box>
              ))}
            </Alert>
          )}

          {atual.estado === "nao_contratado" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Este módulo não faz parte do contrato da sua empresa. Quem resolve
              é o comercial da UpOwl, não o suporte técnico.
            </Alert>
          )}

          {!!atual.tabelas.length && (
            // NOME DE TABELA NAO E INFORMACAO PARA QUEM USA.
            //
            // A tela listava `bridge_customer_segment`, `dim_customer`,
            // `fact_sales_line`... para o executivo do cliente. Ele nao tem o
            // que fazer com isso, e o detalhe escondia o unico dado util da
            // secao: quantas chegaram. Quem precisa do nome abre o Ambiente.
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {atual.tabelas.length} tabela{atual.tabelas.length === 1 ? "" : "s"} publicada
                {atual.tabelas.length === 1 ? "" : "s"} para este módulo.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Honestidade sobre o que ainda não está aqui. Sem esta nota, a primeira
          pergunta é "onde ajusto o horizonte de compra?". */}
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Os ajustes de cálculo deste módulo (horizonte de compra, dias de
        cobertura) ainda não são configuráveis aqui: hoje esses números vêm
        calculados do sistema da sua empresa. Quando passarem a ser nossos,
        aparecem nesta tela.
      </Alert>

      {!!outros.length && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Outros módulos
          </Typography>
          <Stack spacing={1}>
            {outros.map((m) => {
              const v = VISUAL[m.estado];
              const OutroIcon = v.Icon;
              return (
                <Paper key={m.chave} variant="outlined"
                       sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <OutroIcon fontSize="small" sx={{ color: v.cor }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{m.rotulo}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      {m.explicacao}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}
    </>
  );
}
